<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de envio DRS de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableUnidade"
                            :options="form.unidades"
                            @change="loadFrom('unidade')"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableInfraestrutura"
                            :options="form.infraestruturas"
                            @change="loadFrom('infraestrutura')"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :disabled="disableCentroCusto"
                            :options="form.centrosCusto"
                            @change="loadFrom('centroCusto')"
                            default-option-text="Todos"
                            label="Setor"
                            show-default-option
                            text-field="setorNome"
                            v-model="model.centroCustoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :disabled="disableTipo"
                            :options="form.tipos"
                            @change="loadFrom('tipo')"
                            default-option-text="Todos"
                            label="Tipo"
                            show-default-option
                            text-field="nome"
                            v-model="model.tipoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disablePaciente"
                            :options="form.pacientes"
                            default-option-text="Todos"
                            label="Paciente"
                            show-default-option
                            text-field="nome"
                            v-model="model.pacienteId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedColumns"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                class="table-resultado"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
            >
                <template v-slot:cell(materialDescricao)="{ item }">
                    <div
                        v-for="(material, i) in item.materiais"
                        :key="i"
                    >
                        {{ material.materialDescricao }}
                    </div>
                </template>

                <template v-slot:cell(quantidadeSolicitada)="{ item }">
                    <div
                        v-for="(material, i) in item.materiais"
                        :key="i"
                    >
                        {{ material.quantidadeSolicitada }}
                    </div>
                </template>

                <template v-slot:afterTable>
                    <div class="table-resultado-header-footer d-flex justify-content-around">
                        <span><strong>Total de pacientes:</strong> {{ getSafely(() => totalizadores.pacientes, 0) }}</span>
                        <span><strong>Total de processos:</strong> {{ getSafely(() => totalizadores.processos, 0) }}</span>
                        <span><strong>Total de medicamentos:</strong> {{ getSafely(() => totalizadores.medicamentos, 0) }}</span>
                    </div>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { repository, error, data, object, stringCase, file } from '@/mixins'

export default {
    computed: {
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedColumns.includes(c.key))
        },
        validations() {

            return {
                periodo: { required: true },
            }
        },
        disableUnidade() {

            return !this.isPeriodoSelecionado
        },
        disableInfraestrutura() {

            return !this.isPeriodoSelecionado
                || this.model.unidadeId === null
        },
        disableCentroCusto() {

            return !this.isPeriodoSelecionado
                || this.model.infraestruturaId === null
        },
        disableTipo() {

            return !this.isPeriodoSelecionado
        },
        disablePaciente() {

            return !this.isPeriodoSelecionado
        },
        isPeriodoSelecionado() {

            return this.model !== null
                && (this.model.periodo.start instanceof Date)
                && (this.model.periodo.end instanceof Date)
        },
    },
    created() {

        this.repositories = {
            relatorioAltoCustoProcesso: this.getConectaSusRepository('farmacia.relatorioAltoCustoProcesso'),
            impressao: this.getConectaSusRepository('impressao.farmacia'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            selectedAll: false,
            indeterminate: true,
            selectedColumns: [
                'pacienteCpf',
                'pacienteCns',
            ],
            columns: [
                { key: 'pacienteNome', label: 'Paciente', sortable: true, fixed: true },
                { key: 'pacienteCpf', label: 'CPF', sortable: true, class: 'col-min' },
                { key: 'pacienteCns', label: 'CNS', sortable: true, class: 'col-min' },
                { key: 'unidade', label: 'Unidade'},
                { key: 'infraestrutura', label: 'Local de atendimento'},
                { key: 'setor', label: 'Setor', sortable: true },
                { key: 'tipo', label: 'Tipo', sortable: true, class: 'col-min', fixed: true },
                { key: 'materialDescricao', label: 'Medicamento', fixed: true },
                { key: 'quantidadeSolicitada', label: 'Quantidade', class: 'col-min text-center', fixed: true },
            ],
            itens: [],
            totalizadores: null,
            sortBy: 'pacienteNome',
            sortDesc: false,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioAltoCustoProcesso.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.periodo', {
                deep: true,
                handler(periodo) {

                    if (periodo.start === null || periodo.end === null) {

                        this.resetAfterPeriodo()
                    }
                    else {

                        this.loadFrom('periodo')
                    }
                }
            })
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioAltoCustoProcesso.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(field) {

            if (field == 'periodo') {

                return
            }

            const fields = [
                'unidadeId',
                'infraestruturaId',
                'centroCustoId',
                'tipoId',
                'pacienteId',
            ]

            fields.slice(fields.indexOf(`${field}Id`) + 1, fields.length)
                .forEach(key => this.$set(this.model, key, null))
        },
        search() {

            this.setLoading()

            this.repositories.relatorioAltoCustoProcesso.index(this.prepareData())
                .then(({ data }) => {

                    this.itens = data.processos
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        resetAfterPeriodo() {

            this.form = this.clone(this.initials.form)

            const model = this.clone(this.initials.model)

            delete model.periodo

            Object.keys(model).forEach(key => this.$set(this.model, key, model[key]))
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioAltoCustoProcesso(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
            data.sortDesc = this.sortDesc

            data.selectedColumns = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.formateDates(data.periodo, ['start', 'end'], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedColumns = selected ? this.optionalColumns.map(c => c.key) : []
        },
        onSortChanged(context) {

            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
    },
    mixins: [repository, error, data, object, stringCase, file],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
                this.totalizadores = null
            }
        },
        selectedColumns(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}

.table-resultado >>> .table-resultado-header-footer {
    color: var(--gray-dark);
    font-weight: 600;
    background: #f1f1f1;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
}
</style>